import { DialogController } from '../controllers/dialog';
import { VersionDialogController } from '../controllers/footer';
import { TroubleshootingController } from '../controllers/troubleshooting';
import { bufferToUrl, hasValue, supportsPassive, u8aToHex } from '../helpers';
import { emojify } from '../helpers/emoji';
import { publicKeyGrid } from '../helpers/public_key';
import { controllerModelHasMembers, isContactReceiver } from '../typeguards';
class SendFileController extends DialogController {
    caption;
    sendAsFile = false;
    preview = null;
    previewDataUrl = null;
    static $inject = ['$scope', '$mdDialog', 'LogService', 'ThemeService', 'preview'];
    constructor($scope, $mdDialog, logService, themeService, preview) {
        super($scope, $mdDialog, themeService);
        const log = logService.getLogger('SendFile-C');
        this.preview = preview;
        if (preview !== null) {
            this.previewDataUrl = bufferToUrl(this.preview.data, this.preview.fileType, log);
        }
    }
    send() {
        this.hide({
            caption: this.caption,
            sendAsFile: this.sendAsFile,
            previewDataUrl: this.previewDataUrl,
        });
    }
    keypress($event) {
        if ($event.key === 'Enter') {
            this.send();
        }
    }
    hasPreview() {
        return this.previewDataUrl !== null && this.previewDataUrl !== undefined;
    }
}
export class DeviceUnreachableController extends DialogController {
    $rootScope;
    $window;
    $translate;
    stateService;
    webClientService;
    log;
    retrying = false;
    progress = 0;
    highPriorityPushes;
    static $inject = [
        '$rootScope', '$window', '$mdDialog', '$translate',
        'StateService', 'ThemeService', 'WebClientService', 'LogService',
    ];
    constructor($rootScope, $window, $mdDialog, $translate, stateService, themeService, webClientService, logService) {
        super($rootScope, $mdDialog, themeService);
        this.$rootScope = $rootScope;
        this.$window = $window;
        this.$translate = $translate;
        this.stateService = stateService;
        this.webClientService = webClientService;
        this.log = logService.getLogger('DeviceUnreachableDialog-C');
        this.highPriorityPushes = !this.webClientService.hasAppleNonVoipPushToken();
        this.log.info(`Showing "device unreachable" dialog (canRetry=${this.canRetry})`);
    }
    get canRetry() {
        switch (this.webClientService.salty.state) {
            case 'closing':
            case 'closed':
                return false;
            default:
                return true;
        }
    }
    async retry() {
        this.log.debug('Retrying...');
        this.stateService.attempt = 0;
        const [expectedPeriodMaxMs, pushSessionPromise] = this.webClientService.sendPush();
        this.retrying = true;
        this.progress = 0;
        const interval = setInterval(() => this.$rootScope.$apply(() => ++this.progress), expectedPeriodMaxMs / 100);
        try {
            await pushSessionPromise;
        }
        finally {
            clearInterval(interval);
            this.$rootScope.$apply(() => this.retrying = false);
        }
    }
    reload() {
        this.log.info('Reloading page');
        this.$window.location.reload();
    }
    close() {
        this.log.info('Dialog dismissed by user');
        this.cancel();
    }
}
export class PushRejectedDialogController extends DialogController {
    log;
    $window;
    trustedKeyStore;
    static $inject = [
        '$scope', '$mdDialog', '$window', 'ThemeService', 'TrustedKeyStore', 'LogService',
    ];
    constructor($scope, $mdDialog, $window, themeService, trustedKeyStore, logService) {
        super($scope, $mdDialog, themeService);
        this.$window = $window;
        this.log = logService.getLogger('PushRejectedDialog-C');
        this.trustedKeyStore = trustedKeyStore;
    }
    forget() {
        this.log.info('Forgetting stored session');
        this.trustedKeyStore.clearTrustedKey();
        this.cancel();
        this.$window.location.reload();
    }
}
class SettingsController extends DialogController {
    $window;
    settingsService;
    notificationService;
    desktopNotifications;
    notificationApiAvailable;
    notificationPermission;
    notificationPreview;
    notificationSound;
    submitKey;
    userInterface;
    static $inject = [
        '$scope', '$mdDialog', '$window', 'SettingsService', 'ThemeService', 'NotificationService',
    ];
    constructor($scope, $mdDialog, $window, settingsService, themeService, notificationService) {
        super($scope, $mdDialog, themeService);
        this.$window = $window;
        this.settingsService = settingsService;
        this.notificationService = notificationService;
        this.desktopNotifications = notificationService.getWantsNotifications();
        this.notificationApiAvailable = notificationService.isNotificationApiAvailable();
        this.notificationPermission = notificationService.getNotificationPermission();
        this.notificationPreview = notificationService.getWantsPreview();
        this.notificationSound = notificationService.getWantsSound();
        this.submitKey = settingsService.composeArea.getSubmitKey().toString();
        this.userInterface = settingsService.userInterface.getUserInterface().toString();
    }
    setWantsNotifications(desktopNotifications) {
        this.notificationService.setWantsNotifications(desktopNotifications);
    }
    setWantsPreview(notificationPreview) {
        this.notificationService.setWantsPreview(notificationPreview);
    }
    setWantsSound(notificationSound) {
        this.notificationService.setWantsSound(notificationSound);
    }
    setSubmitKey(submitKey) {
        this.settingsService.composeArea.setSubmitKey(submitKey);
    }
    setUserInterface(userInterface) {
        this.settingsService.userInterface.setUserInterface(userInterface);
    }
}
class ConversationController {
    name = 'navigation';
    $stateParams;
    $state;
    $scope;
    $rootScope;
    $filter;
    $translate;
    webClientService;
    receiverService;
    stateService;
    mimeService;
    timeoutService;
    $mdDialog;
    $mdToast;
    log;
    controllerModel;
    domChatElement;
    showScrollJump = false;
    receiver;
    _conversation;
    type;
    messages;
    initialized = false;
    allMentions = [];
    currentMentions = [];
    currentMentionFilterWord = null;
    selectedMention = null;
    message = '';
    lastReadMsg = null;
    msgReadReportPending = false;
    hasMore = true;
    latestRefMsgId = null;
    allText;
    initialData = {
        draft: '',
        initialText: '',
    };
    locked = false;
    maxTextLength;
    isTyping = () => false;
    uploading = {
        enabled: false,
        value1: 0,
        value2: 0,
    };
    static $inject = [
        '$stateParams', '$scope', '$rootScope',
        '$mdDialog', '$mdToast', '$translate', '$filter',
        '$state', '$transitions',
        'LogService', 'WebClientService', 'StateService', 'ReceiverService', 'MimeService',
        'VersionService', 'ControllerModelService', 'TimeoutService',
    ];
    constructor($stateParams, $scope, $rootScope, $mdDialog, $mdToast, $translate, $filter, $state, $transitions, logService, webClientService, stateService, receiverService, mimeService, versionService, controllerModelService, timeoutService) {
        this.$stateParams = $stateParams;
        this.webClientService = webClientService;
        this.receiverService = receiverService;
        this.stateService = stateService;
        this.mimeService = mimeService;
        this.timeoutService = timeoutService;
        this.log = logService.getLogger('Conversation-C');
        this.$state = $state;
        this.$scope = $scope;
        this.$filter = $filter;
        this.$rootScope = $rootScope;
        this.$mdDialog = $mdDialog;
        this.$mdToast = $mdToast;
        this.$translate = $translate;
        this.$mdDialog.cancel();
        this.maxTextLength = this.webClientService.getMaxTextLength();
        this.allText = this.$translate.instant('messenger.ALL');
        $transitions.onStart({}, function (trans) {
            const $mdDialogInner = trans.injector().get('$mdDialog');
            $mdDialogInner.cancel();
        });
        versionService.checkForUpdate();
        if (stateService.state === 'error') {
            this.log.debug('WebClient not yet running, redirecting to welcome screen');
            $state.go('welcome');
            return;
        }
        if (!this.locked) {
            this.domChatElement = document.querySelector('#conversation-chat');
            this.domChatElement.addEventListener('scroll', () => {
                $rootScope.$apply(() => {
                    this.updateScrollJump();
                });
            }, supportsPassive() ? { passive: true } : false);
        }
        try {
            this.receiver = webClientService.receivers.getData({ type: $stateParams.type, id: $stateParams.id });
            this._conversation = this.webClientService.conversations.find(this.receiver);
            this.type = $stateParams.type;
            if (this.receiver.type === undefined) {
                this.receiver.type = this.type;
            }
            const mode = "chat";
            switch (this.receiver.type) {
                case 'me':
                    this.controllerModel = controllerModelService.me(this.receiver, mode);
                    break;
                case 'contact':
                    this.controllerModel = controllerModelService.contact(this.receiver, mode);
                    break;
                case 'group':
                    this.controllerModel = controllerModelService.group(this.receiver, mode);
                    break;
                case 'distributionList':
                    this.controllerModel = controllerModelService.distributionList(this.receiver, mode);
                    break;
                default:
                    this.log.error('Cannot initialize controller model:', 'Invalid receiver type "' + this.receiver.type + '"');
                    $state.go('messenger.home');
                    return;
            }
            if (this.controllerModel.canChat() === false) {
                this.log.warn('Cannot chat with this receiver, redirecting to home');
                $state.go('messenger.home');
                return;
            }
            this.locked = this.receiver.locked;
            this.receiverService.setActive(this.receiver);
            if (!this.receiver.locked) {
                let latestHeight = 0;
                this.messages = this.webClientService.messages.register(this.receiver, this.$scope, (e, allMessages, hasMore) => {
                    this.messages = allMessages;
                    const wasInitialized = this.initialized;
                    this.initialized = true;
                    this.hasMore = hasMore;
                    if (!wasInitialized) {
                        this.webClientService.messages.updateFirstUnreadMessage(this.receiver);
                    }
                    if (this.latestRefMsgId !== null) {
                        this.domChatElement.scrollTop = this.domChatElement.scrollHeight - latestHeight;
                        this.latestRefMsgId = null;
                    }
                    latestHeight = this.domChatElement.scrollHeight;
                });
                if (this.conversation !== null) {
                    this.webClientService.sendActiveConversation(this.conversation);
                }
                this.webClientService.messages.updateFirstUnreadMessage(this.receiver);
                if (this.type === 'group' && controllerModelHasMembers(this.controllerModel)) {
                    this.allMentions.push({
                        identity: null,
                        query: this.$translate.instant('messenger.ALL').toLowerCase(),
                        isAll: true,
                    });
                    this.controllerModel.getMembers().forEach((identity) => {
                        const contactReceiver = this.webClientService.contacts.get(identity);
                        if (contactReceiver) {
                            this.allMentions.push({
                                identity: identity,
                                query: (contactReceiver.displayName + ' ' + identity).toLowerCase(),
                                isAll: false,
                            });
                        }
                    });
                }
                this.initialData = {
                    draft: webClientService.getDraft(this.receiver),
                    initialText: $stateParams.initParams ? $stateParams.initParams.text : '',
                };
                if (isContactReceiver(this.receiver)) {
                    this.isTyping = () => this.webClientService.isTyping(this.receiver);
                }
                if (this.webClientService.messages.getList(this.receiver).length === 0) {
                    this.requestMessages();
                }
            }
        }
        catch (error) {
            this.log.error('Could not set receiver and type:', error);
            $state.go('messenger.home');
        }
        $scope.$watch(() => {
            return this.receiver.locked;
        }, () => {
            if (this.locked !== this.receiver.locked) {
                $state.reload().catch((error) => {
                    this.log.error('Unable to reload state:', error);
                });
            }
        });
    }
    get conversation() {
        if (!hasValue(this._conversation)) {
            this._conversation = this.webClientService.conversations.find(this.receiver);
        }
        return this._conversation;
    }
    isEnabled() {
        return this.type !== 'group'
            || !this.receiver.disabled;
    }
    isQuoting() {
        return this.getQuote() !== undefined;
    }
    getQuote() {
        return this.webClientService.getQuote(this.receiver);
    }
    cancelQuoting() {
        this.webClientService.setQuote(this.receiver, null);
    }
    showError(errorMessage, hideDelayMs = 3000) {
        if (errorMessage === undefined || errorMessage.length === 0) {
            errorMessage = this.$translate.instant('error.ERROR_OCCURRED');
        }
        this.$mdToast.show(this.$mdToast.simple()
            .textContent(errorMessage)
            .position('bottom center')
            .hideDelay(hideDelayMs));
    }
    showMessage(msgTranslation, hideDelayMs = 3000) {
        this.$mdToast.show(this.$mdToast.simple()
            .textContent(this.$translate.instant(msgTranslation))
            .position('bottom center')
            .hideDelay(hideDelayMs));
    }
    submit = (type, contents) => {
        return new Promise((resolve, reject) => {
            if (!this.webClientService.readyToSubmit) {
                this.showError(this.$translate.instant('error.NO_CONNECTION'));
                return reject();
            }
            let success = true;
            const nextCallback = (index) => {
                if (index === contents.length - 1) {
                    if (success) {
                        resolve();
                    }
                    else {
                        reject('Message sending unsuccessful');
                    }
                }
            };
            switch (type) {
                case 'file':
                    const fileCount = contents.length;
                    const showCaption = fileCount === 1;
                    let showSendAsFileCheckbox = false;
                    for (const msg of contents) {
                        if (!msg.fileType) {
                            msg.fileType = 'application/octet-stream';
                        }
                        const isImage = this.mimeService.isImage(msg.fileType);
                        const isAudio = this.mimeService.isAudio(msg.fileType, this.webClientService.clientInfo.os);
                        const isVideo = this.mimeService.isVideo(msg.fileType);
                        if (isImage || isAudio || isVideo) {
                            showSendAsFileCheckbox = true;
                        }
                    }
                    let preview = null;
                    if (fileCount === 1) {
                        const msg = contents[0];
                        if (this.mimeService.isImage(msg.fileType)) {
                            preview = msg;
                        }
                    }
                    let title;
                    const senderName = emojify(this.$filter('emptyToPlaceholder')(this.receiver.displayName, '-'));
                    if (fileCount === 1) {
                        title = this.$translate.instant('messenger.CONFIRM_FILE_SEND', { senderName: senderName });
                    }
                    else {
                        title = this.$translate.instant('messenger.CONFIRM_FILE_SEND_MULTI', {
                            fileCount: fileCount,
                            senderName: senderName,
                        });
                    }
                    const placeholder = this.$translate.instant('messenger.CONFIRM_FILE_CAPTION');
                    const confirmSendAsFile = this.$translate.instant('messenger.CONFIRM_SEND_AS_FILE');
                    this.$mdDialog.show({
                        clickOutsideToClose: false,
                        locals: { preview: preview },
                        controller: 'SendFileController',
                        controllerAs: 'ctrl',
                        template: `
                            <md-dialog class="send-file-dialog">
                                <md-dialog-content class="md-dialog-content">
                                    <h2 class="md-title">${title}</h2>
                                    <img class="preview" ng-if="ctrl.hasPreview()" ng-src="{{ ctrl.previewDataUrl }}">
                                    <md-input-container md-no-float class="input-caption md-prompt-input-container" ng-show="${showCaption}">
                                        <input maxlength="1000" md-autofocus ng-keypress="ctrl.keypress($event)" ng-model="ctrl.caption" placeholder="${placeholder}" aria-label="${placeholder}">
                                    </md-input-container>
                                    <md-input-container md-no-float class="input-send-as-file md-prompt-input-container" ng-show="${showSendAsFileCheckbox}">
                                        <md-checkbox ng-model="ctrl.sendAsFile" aria-label="${confirmSendAsFile}">
                                            ${confirmSendAsFile}
                                        </md-checkbox>
                                    </md-input-container>
                                </md-dialog-content>
                                <md-dialog-actions>
                                    <button class="md-primary md-cancel-button md-button" md-ink-ripple type="button" ng-click="ctrl.cancel()">
                                        <span translate>common.CANCEL</span>
                                    </button>
                                    <button class="md-primary md-cancel-button md-button" md-ink-ripple type="button" ng-click="ctrl.send()">
                                        <span translate>common.SEND</span>
                                    </button>
                                </md-dialog-actions>
                            </md-dialog>
                        `,
                    }).then((data) => {
                        const caption = data.caption;
                        const sendAsFile = data.sendAsFile;
                        const options = { previewDataUrl: data.previewDataUrl || undefined };
                        contents.forEach((msg, index) => {
                            if (caption !== undefined && caption.length > 0) {
                                msg.caption = caption;
                            }
                            msg.sendAsFile = sendAsFile;
                            this.webClientService.sendMessage(this.$stateParams, type, msg, options)
                                .then(() => {
                                nextCallback(index);
                            })
                                .catch((error) => {
                                this.log.error(error);
                                this.showError(error);
                                success = false;
                                nextCallback(index);
                            });
                        });
                    }, angular.noop);
                    break;
                case 'text':
                    contents.forEach((msg, index) => {
                        const quote = this.webClientService.getQuote(this.receiver);
                        if (hasValue(quote)) {
                            msg.quote = quote;
                        }
                        this.webClientService.setQuote(this.receiver, null);
                        this.webClientService.sendMessage(this.$stateParams, type, msg)
                            .then(() => {
                            nextCallback(index);
                        })
                            .catch((error) => {
                            this.log.error(error);
                            this.showError(error);
                            success = false;
                            nextCallback(index);
                        });
                    });
                    return;
                default:
                    this.log.warn('Invalid message type:', type);
                    reject();
            }
        });
    };
    onTyping = (text) => {
        this.webClientService.setDraft(this.receiver, text);
    };
    getSelectedMention = () => {
        if (this.selectedMention === null
            || this.selectedMention < 0
            || this.selectedMention > this.currentMentions.length - 1) {
            return null;
        }
        return this.currentMentions[this.selectedMention];
    };
    showMentionSelector = () => {
        return this.type === 'group'
            && this.currentMentionFilterWord != null
            && this.currentMentions.length > 0;
    };
    onComposeKeyDown = (ev) => {
        return true;
    };
    onMentionSelected(identity = null) {
        this.$rootScope.$broadcast('onMentionSelected', {
            query: '@' + this.currentMentionFilterWord,
            mention: '@[' + (identity === null ? '@@@@@@@@' : identity.toUpperCase()) + ']',
        });
    }
    onUploading = (inProgress, percentCurrent = null, percentFull = null) => {
        this.uploading.enabled = inProgress;
        this.uploading.value1 = Number(percentCurrent);
        this.uploading.value2 = Number(percentCurrent);
    };
    startTyping = () => {
        if (isContactReceiver(this.receiver)) {
            this.webClientService.sendMeIsTyping(this.receiver, true);
        }
    };
    stopTyping = () => {
        if (isContactReceiver(this.receiver)) {
            this.webClientService.sendMeIsTyping(this.receiver, false);
        }
    };
    topOfChat() {
        this.requestMessages();
    }
    requestMessages() {
        const refMsgId = this.webClientService.requestMessages(this.$stateParams);
        if (hasValue(refMsgId)) {
            this.latestRefMsgId = refMsgId;
        }
        else {
            this.latestRefMsgId = null;
        }
    }
    showReceiver(ev) {
        this.$state.go('messenger.home.detail', this.receiver);
    }
    hasMoreMessages() {
        return this.hasMore;
    }
    msgRead(message) {
        if (message.type === 'status') {
            return;
        }
        if (message.isOutbox) {
            return;
        }
        if (!message.unread) {
            return;
        }
        if (this.lastReadMsg === null || message.sortKey >= this.lastReadMsg.sortKey) {
            this.lastReadMsg = message;
        }
        if (!this.msgReadReportPending) {
            this.msgReadReportPending = true;
            const receiver = structuredClone(this.receiver);
            receiver.type = this.type;
            this.timeoutService.register(() => {
                this.webClientService.requestRead(receiver, this.lastReadMsg);
                this.msgReadReportPending = false;
            }, 300, false, 'requestRead');
        }
    }
    goBack() {
        this.receiverService.setActive(undefined);
        this.$state.go('messenger.home');
    }
    scrollDown() {
        this.domChatElement.scrollTop = this.domChatElement.scrollHeight;
    }
    updateScrollJump() {
        const chat = this.domChatElement;
        this.showScrollJump = chat.scrollHeight - (chat.scrollTop + chat.offsetHeight) > 1;
    }
    pinConversation() {
        if (!hasValue(this.conversation)) {
            this.log.warn('Cannot pin, no conversation exists');
            return;
        }
        this.webClientService
            .modifyConversation(this.conversation, true)
            .then(() => this.showMessage('messenger.PINNED_CONVERSATION_OK'))
            .catch((e) => {
            this.showMessage('messenger.PINNED_CONVERSATION_ERROR');
            this.log.error('Pinning conversation failed: ' + e);
        });
    }
    unpinConversation() {
        if (!hasValue(this.conversation)) {
            this.log.warn('Cannot unpin, no conversation exists');
            return;
        }
        this.webClientService
            .modifyConversation(this.conversation, false)
            .then(() => this.showMessage('messenger.UNPINNED_CONVERSATION_OK'))
            .catch((e) => {
            this.showMessage('messenger.UNPINNED_CONVERSATION_ERROR');
            this.log.error('Unpinning conversation failed: ' + e);
        });
    }
}
class AboutDialogController extends DialogController {
    config;
    static $inject = ['$scope', '$mdDialog', 'ThemeService', 'CONFIG'];
    constructor($scope, $mdDialog, themeService, config) {
        super($scope, $mdDialog, themeService);
        this.config = config;
    }
}
class NavigationController {
    name = 'navigation';
    minimalUserInterface = false;
    webClientService;
    receiverService;
    stateService;
    trustedKeyStoreService;
    notificationService;
    activeTab = 'conversations';
    searchVisible = false;
    searchText = '';
    $mdDialog;
    $translate;
    $state;
    static $inject = [
        '$scope', '$state', '$mdDialog', '$translate',
        'LogService', 'WebClientService', 'StateService', 'ReceiverService', 'NotificationService', 'TrustedKeyStore',
        'SettingsService'
    ];
    constructor($scope, $state, $mdDialog, $translate, logService, webClientService, stateService, receiverService, notificationService, trustedKeyStoreService, settingsService) {
        const log = logService.getLogger('Navigation-C');
        if (stateService.state === 'error') {
            log.debug('WebClient not yet running, redirecting to welcome screen');
            $state.go('welcome');
            return;
        }
        this.minimalUserInterface = this.isMinimalUserInterface(settingsService.userInterface.getUserInterface());
        settingsService.userInterfaceChange.attach((newUserInterface) => {
            $scope.$apply(() => this.minimalUserInterface = this.isMinimalUserInterface(newUserInterface));
        });
        this.webClientService = webClientService;
        this.receiverService = receiverService;
        this.stateService = stateService;
        this.trustedKeyStoreService = trustedKeyStoreService;
        this.notificationService = notificationService;
        this.$mdDialog = $mdDialog;
        this.$translate = $translate;
        this.$state = $state;
    }
    contacts() {
        const contacts = [];
        for (const contact of this.webClientService.contacts.values()) {
            if (contact.id === this.webClientService.receivers.me.id) {
                continue;
            }
            if (contact.hidden === true) {
                continue;
            }
            contacts.push(contact);
        }
        return contacts;
    }
    matches(haystack, needle) {
        return haystack.toLowerCase().replace('\n', ' ').indexOf(needle.trim().toLowerCase()) !== -1;
    }
    searchConversation = (value, index, array) => {
        return this.searchText === ''
            || this.matches(value.receiver.displayName, this.searchText)
            || (value.latestMessage && value.latestMessage.body
                && this.matches(value.latestMessage.body, this.searchText))
            || (value.receiver.id.length === 8 && this.matches(value.receiver.id, this.searchText));
    };
    searchContact = (value, index, array) => {
        return this.searchText === ''
            || value.displayName.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1
            || value.id.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1;
    };
    isVisible(conversation) {
        return conversation.receiver.visible;
    }
    conversations() {
        return this.webClientService.conversations.get();
    }
    isActive(value) {
        return this.receiverService.isConversationActive(value);
    }
    startupDone() {
        return this.webClientService.startupDone;
    }
    hideInactiveAndRevokedContacts() {
        return !this.webClientService.appConfig.showInactiveIDs;
    }
    showDialog(name, ev, controller = DialogController) {
        this.$mdDialog.show({
            controller: controller,
            controllerAs: 'ctrl',
            templateUrl: 'partials/dialog.' + name + '.html',
            parent: angular.element(document.body),
            targetEvent: ev,
            clickOutsideToClose: true,
            fullscreen: true,
        });
    }
    about(ev) {
        this.showDialog('about', ev, AboutDialogController);
    }
    version(ev) {
        this.showDialog('version', ev, VersionDialogController);
    }
    troubleshooting() {
        this.$mdDialog.show({
            controller: TroubleshootingController,
            controllerAs: 'ctrl',
            templateUrl: 'partials/dialog.troubleshooting.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            fullscreen: true,
        });
    }
    settings(ev) {
        this.$mdDialog.show({
            controller: SettingsController,
            controllerAs: 'ctrl',
            templateUrl: 'partials/dialog.settings.html',
            parent: angular.element(document.body),
            targetEvent: ev,
            clickOutsideToClose: true,
            fullscreen: true,
        });
    }
    showProfile(ev) {
        this.receiverService.setActive(undefined);
        this.$state.go('messenger.home.detail', this.webClientService.me);
    }
    isPersistent() {
        return this.trustedKeyStoreService.hasTrustedKey();
    }
    closeSession(ev) {
        const confirm = this.$mdDialog.confirm()
            .title(this.$translate.instant('common.SESSION_CLOSE'))
            .textContent(this.$translate.instant('common.CONFIRM_CLOSE_BODY'))
            .targetEvent(ev)
            .ok(this.$translate.instant('common.YES'))
            .cancel(this.$translate.instant('common.CANCEL'));
        this.$mdDialog.show(confirm).then(() => {
            this.webClientService.stop({
                reason: "stop",
                send: true,
                close: 'welcome',
                connectionBuildupState: 'closed',
            });
        }, () => {
        });
    }
    deleteSession(ev) {
        const confirm = this.$mdDialog.confirm()
            .title(this.$translate.instant('common.SESSION_DELETE'))
            .textContent(this.$translate.instant('common.CONFIRM_DELETE_CLOSE_BODY'))
            .targetEvent(ev)
            .ok(this.$translate.instant('common.YES'))
            .cancel(this.$translate.instant('common.CANCEL'));
        this.$mdDialog.show(confirm).then(() => {
            this.webClientService.stop({
                reason: "delete",
                send: true,
                close: 'welcome',
                connectionBuildupState: 'closed',
            });
        }, () => {
        });
    }
    addContact(ev) {
        this.$state.go('messenger.home.create', {
            type: 'contact',
        });
    }
    createGroup(ev) {
        this.$state.go('messenger.home.create', {
            type: 'group',
        });
    }
    createDistributionList(ev) {
        this.$state.go('messenger.home.create', {
            type: 'distributionList',
        });
    }
    toggleSearch() {
        this.searchVisible = !this.searchVisible;
    }
    toggleSearchOff() {
        if (this.searchVisible) {
            this.searchVisible = false;
        }
    }
    clearSearch(ev) {
        if (ev.key === 'Escape') {
            if (this.searchText === '') {
                this.searchVisible = false;
            }
            else {
                this.searchText = '';
            }
        }
    }
    getMe() {
        return this.webClientService.me;
    }
    showCreateDistributionListButton() {
        return this.webClientService.appCapabilities.distributionLists;
    }
    dndModeSimplified(conversation) {
        return this.notificationService.getDndModeSimplified(conversation);
    }
    isMinimalUserInterface(userInterface) {
        return userInterface === "minimal";
    }
}
class MessengerController {
    name = 'messenger';
    receiverService;
    $state;
    webClientService;
    static $inject = [
        '$scope', '$state', '$mdDialog', '$translate',
        'LogService', 'StateService', 'ReceiverService', 'WebClientService', 'ControllerService',
    ];
    constructor($scope, $state, $mdDialog, $translate, logService, stateService, receiverService, webClientService, controllerService) {
        const log = logService.getLogger('Messenger-C');
        if (stateService.state === 'error') {
            log.debug('WebClient not yet running, redirecting to welcome screen');
            $state.go('welcome');
            return;
        }
        controllerService.setControllerName('messenger');
        this.receiverService = receiverService;
        this.$state = $state;
        this.webClientService = webClientService;
        $scope.$watch(() => webClientService.alerts, (alerts) => {
            if (alerts.length > 0) {
                angular.forEach(alerts, (alert) => {
                    $mdDialog.show($mdDialog.alert()
                        .clickOutsideToClose(true)
                        .title(alert.type)
                        .textContent(alert.message)
                        .ok($translate.instant('common.OK')));
                });
                webClientService.alerts = [];
            }
        }, true);
        this.webClientService.setReceiverListener({
            onConversationRemoved(receiver) {
                switch ($state.current.name) {
                    case 'messenger.home.conversation':
                    case 'messenger.home.detail':
                    case 'messenger.home.edit':
                        if ($state.params !== undefined
                            && $state.params.type !== undefined
                            && $state.params.id !== undefined) {
                            if ($state.params.type === receiver.type
                                && $state.params.id === receiver.id) {
                                $state.go('messenger.home');
                            }
                        }
                        break;
                    default:
                        log.debug('Ignored onRemoved event for state', $state.current.name);
                }
            },
        });
    }
    showDetail() {
        return !this.$state.is('messenger.home');
    }
}
class QrDialogController extends DialogController {
    profile;
    qrCode;
    static $inject = ['$scope', '$mdDialog', 'ThemeService', 'WebClientService'];
    constructor($scope, $mdDialog, themeService, webClientService) {
        super($scope, $mdDialog, themeService);
        this.profile = webClientService.me;
        this.qrCode = {
            errorCorrectionLevel: 'L',
            size: '400px',
            data: '3mid:'
                + this.profile.id
                + ','
                + u8aToHex(new Uint8Array(this.profile.publicKey)),
        };
    }
}
class ReceiverDetailController {
    $mdDialog;
    $scope;
    $state;
    contactService;
    webClientService;
    receiver;
    me;
    title;
    publicKeyGrid = '';
    showGroups = false;
    showDistributionLists = false;
    inGroups = [];
    inDistributionLists = [];
    hasSystemEmails = false;
    hasSystemPhones = false;
    isWorkReceiver = false;
    showBlocked = () => false;
    controllerModel;
    static $inject = [
        '$scope', '$stateParams', '$state', '$mdDialog', '$translate',
        'LogService', 'WebClientService', 'ContactService', 'ControllerModelService',
    ];
    constructor($scope, $stateParams, $state, $mdDialog, $translate, logService, webClientService, contactService, controllerModelService) {
        this.$mdDialog = $mdDialog;
        this.$scope = $scope;
        this.$state = $state;
        this.contactService = contactService;
        this.webClientService = webClientService;
        this.receiver = webClientService.receivers.getData($stateParams);
        this.me = webClientService.me;
        const log = logService.getLogger('ReceiverDetail-C');
        if (isContactReceiver(this.receiver)) {
            const contactReceiver = this.receiver;
            this.contactService.requiredDetails(contactReceiver)
                .then(() => {
                this.hasSystemEmails = contactReceiver.systemContact !== undefined
                    && contactReceiver.systemContact.emails.length > 0;
                this.hasSystemPhones = contactReceiver.systemContact !== undefined &&
                    contactReceiver.systemContact.phoneNumbers.length > 0;
            })
                .catch((error) => {
                log.error(`Contact detail request has been rejected: ${error}`);
            });
            this.isWorkReceiver = contactReceiver.identityType === 1;
            webClientService.groups.forEach((groupReceiver) => {
                if (groupReceiver.members.indexOf(contactReceiver.id) !== -1) {
                    this.inGroups.push(groupReceiver);
                    this.showGroups = true;
                }
            });
            webClientService.distributionLists.forEach((distributionListReceiver) => {
                if (distributionListReceiver.members.indexOf(contactReceiver.id) !== -1) {
                    this.inDistributionLists.push(distributionListReceiver);
                    this.showDistributionLists = true;
                }
            });
            this.showBlocked = () => contactReceiver.isBlocked;
        }
        switch (this.receiver.type) {
            case 'me':
                const meReceiver = this.receiver;
                this.controllerModel = controllerModelService.me(meReceiver, "view");
                this.publicKeyGrid = publicKeyGrid(new Uint8Array(meReceiver.publicKey));
                break;
            case 'contact':
                const contactReceiver = this.receiver;
                this.controllerModel = controllerModelService.contact(contactReceiver, "view");
                this.publicKeyGrid = publicKeyGrid(new Uint8Array(contactReceiver.publicKey));
                break;
            case 'group':
                this.controllerModel = controllerModelService
                    .group(this.receiver, "view");
                break;
            case 'distributionList':
                this.controllerModel = controllerModelService
                    .distributionList(this.receiver, "view");
                break;
            default:
                log.error('Cannot initialize controller model:', 'Invalid receiver type "' + this.receiver.type + '"');
                $state.go('messenger.home');
                return;
        }
        this.controllerModel.setOnRemoved((receiverId) => {
            log.warn('Receiver removed, redirecting to home');
            this.$state.go('messenger.home');
        });
    }
    chat() {
        this.$state.go('messenger.home.conversation', {
            type: this.receiver.type,
            id: this.receiver.id,
            initParams: null,
        });
    }
    edit() {
        if (!this.controllerModel.canEdit()) {
            return;
        }
        this.$state.go('messenger.home.edit', {
            type: this.receiver.type,
            id: this.receiver.id,
            initParams: null,
        });
    }
    showQr() {
        const $mdDialog = this.$mdDialog;
        $mdDialog.show({
            controllerAs: 'ctrl',
            controller: QrDialogController,
            templateUrl: 'partials/dialog.qr.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            fullscreen: true,
        });
    }
    goBack() {
        window.history.back();
    }
}
class ReceiverEditController {
    $mdDialog;
    $scope;
    $state;
    $translate;
    title;
    $timeout;
    future;
    controllerModel;
    type;
    static $inject = [
        '$scope', '$stateParams', '$state', '$mdDialog',
        '$timeout', '$translate', 'LogService', 'WebClientService', 'ControllerModelService',
    ];
    constructor($scope, $stateParams, $state, $mdDialog, $timeout, $translate, logService, webClientService, controllerModelService) {
        this.$scope = $scope;
        this.$mdDialog = $mdDialog;
        this.$state = $state;
        this.$timeout = $timeout;
        this.$translate = $translate;
        const log = logService.getLogger('ReceiverEdit-C');
        const receiver = webClientService.receivers.getData($stateParams);
        switch (receiver.type) {
            case 'me':
                this.controllerModel = controllerModelService.me(receiver, "edit");
                break;
            case 'contact':
                this.controllerModel = controllerModelService.contact(receiver, "edit");
                break;
            case 'group':
                this.controllerModel = controllerModelService.group(receiver, "edit");
                break;
            case 'distributionList':
                this.controllerModel = controllerModelService.distributionList(receiver, "edit");
                break;
            default:
                log.error('Cannot initialize controller model:', 'Invalid receiver type "' + receiver.type + '"');
                $state.go('messenger.home');
                return;
        }
        this.type = receiver.type;
    }
    keypress($event) {
        if ($event.key === 'Enter' && this.controllerModel.isValid()) {
            this.save();
        }
    }
    save() {
        this.future = Future.withMinDuration(this.controllerModel.save(), 100);
        this.future
            .then(() => {
            this.$scope.$apply(() => {
                this.goBack();
            });
        })
            .catch((errorCode) => {
            this.$scope.$apply(() => {
                this.showEditError(errorCode);
            });
        });
    }
    isSaving() {
        return this.future !== undefined && !this.future.done;
    }
    showEditError(errorCode) {
        if (errorCode === undefined) {
            errorCode = 'unknown';
        }
        this.$mdDialog.show(this.$mdDialog.alert()
            .clickOutsideToClose(true)
            .title(this.controllerModel.subject)
            .textContent(this.$translate.instant('validationError.modifyReceiver.' + errorCode))
            .ok(this.$translate.instant('common.OK')));
    }
    goBack() {
        window.history.back();
    }
}
class ReceiverCreateController {
    $mdDialog;
    $scope;
    $timeout;
    $state;
    $mdToast;
    identity = '';
    $translate;
    type;
    future;
    controllerModel;
    static $inject = ['$stateParams', '$mdDialog', '$scope', '$mdToast', '$translate',
        '$timeout', '$state', 'LogService', 'ControllerModelService'];
    constructor($stateParams, $mdDialog, $scope, $mdToast, $translate, $timeout, $state, logService, controllerModelService) {
        this.$mdDialog = $mdDialog;
        this.$scope = $scope;
        this.$timeout = $timeout;
        this.$state = $state;
        this.$mdToast = $mdToast;
        this.$translate = $translate;
        const log = logService.getLogger('ReceiverEdit-C');
        this.type = $stateParams.type;
        switch (this.type) {
            case 'me':
                log.warn('Cannot create own contact');
                $state.go('messenger.home');
                return;
            case 'contact':
                this.controllerModel = controllerModelService.contact(null, "new");
                if ($stateParams.initParams !== null) {
                    this.controllerModel
                        .identity = $stateParams.initParams.identity;
                }
                break;
            case 'group':
                this.controllerModel = controllerModelService.group(null, "new");
                break;
            case 'distributionList':
                this.controllerModel = controllerModelService.distributionList(null, "new");
                break;
            default:
                log.error('Invalid type', this.type);
        }
    }
    isSaving() {
        return this.future !== undefined && !this.future.done;
    }
    goBack() {
        if (!this.isSaving()) {
            window.history.back();
        }
    }
    showAddError(errorCode) {
        if (errorCode === undefined) {
            errorCode = 'unknown';
        }
        this.$mdDialog.show(this.$mdDialog.alert()
            .clickOutsideToClose(true)
            .title(this.controllerModel.subject)
            .textContent(this.$translate.instant('validationError.modifyReceiver.' + errorCode))
            .ok(this.$translate.instant('common.OK')));
    }
    keypress($event) {
        if ($event.key === 'Enter' && this.controllerModel.isValid()) {
            this.create();
        }
    }
    create() {
        this.future = Future.withMinDuration(this.controllerModel.save(), 100);
        this.future
            .then((receiver) => {
            this.$scope.$apply(() => {
                this.$state.go('messenger.home.detail', receiver, { location: 'replace' });
            });
        })
            .catch((errorCode) => {
            this.$scope.$apply(() => {
                this.showAddError(errorCode);
            });
        });
    }
}
angular.module('3ema.messenger', ['ngMaterial'])
    .config(['$stateProvider', function ($stateProvider) {
        $stateProvider
            .state('messenger', {
            abstract: true,
            templateUrl: 'partials/messenger.html',
            controller: 'MessengerController',
            controllerAs: 'ctrl',
        })
            .state('messenger.home', {
            url: '/messenger',
            views: {
                navigation: {
                    templateUrl: 'partials/messenger.navigation.html',
                    controller: 'NavigationController',
                    controllerAs: 'ctrl',
                },
                content: {
                    template: '<div ui-view></div>',
                },
            },
        })
            .state('messenger.home.conversation', {
            url: '/conversation/{type}/{id}',
            templateUrl: 'partials/messenger.conversation.html',
            controller: 'ConversationController',
            controllerAs: 'ctrl',
            params: { initParams: null },
        })
            .state('messenger.home.detail', {
            url: '/conversation/{type}/{id}/detail',
            templateUrl: 'partials/messenger.receiver.html',
            controller: 'ReceiverDetailController',
            controllerAs: 'ctrl',
        })
            .state('messenger.home.edit', {
            url: '/conversation/{type}/{id}/detail/edit',
            templateUrl: 'partials/messenger.receiver.edit.html',
            controller: 'ReceiverEditController',
            controllerAs: 'ctrl',
        })
            .state('messenger.home.create', {
            url: '/receiver/create/{type}',
            templateUrl: 'partials/messenger.receiver.create.html',
            controller: 'ReceiverCreateController',
            controllerAs: 'ctrl',
            params: { initParams: null },
        });
    }])
    .controller('SendFileController', SendFileController)
    .controller('MessengerController', MessengerController)
    .controller('ConversationController', ConversationController)
    .controller('NavigationController', NavigationController)
    .controller('ReceiverDetailController', ReceiverDetailController)
    .controller('ReceiverEditController', ReceiverEditController)
    .controller('ReceiverCreateController', ReceiverCreateController);
